import type { ReactNode } from 'react'
import type { Messages } from '@/features/language/messages'
import { useMessages } from '@/features/language/use-language'
import { Notice } from '@/shared/ui/Notice'
import { ProviderButton } from '@/shared/ui/ProviderButton'
import { PROVIDER_IDS, type ProviderId } from '@/shared/ui/providers'

/** Backend가 실패 화면으로 되돌릴 때 주소에 붙이는 까닭. */
export type LoginFailureReason =
  'declined' | 'email_already_registered' | 'email_unavailable' | 'unavailable'

export type LoginStatus = 'idle' | 'leaving' | 'establishing'

interface LoginScreenProps {
  onStart: (provider: ProviderId) => void
  /** leaving은 provider로 떠나는 중이고 establishing은 돌아와 Session을 세우는 중이다. */
  status?: LoginStatus
  /** 떠나는 중일 때 누른 버튼. 진행 문구를 그 버튼에만 건다. */
  leavingProvider?: ProviderId | null
  reason?: LoginFailureReason | null
  /** 앱 안에서 Session이 끊겨 옮겨져 온 자리인지. 주소에 싣지 않고 화면 안에서만 전한다. */
  sessionExpired?: boolean
  /** 실패가 어느 provider에서 났는지. 이름을 끼우는 문구가 이 값을 읽는다. */
  failedProvider?: ProviderId | null
  termsHref?: string
  privacyHref?: string
}

/**
 * 로그인과 가입을 가르지 않는 단일 진입 화면.
 *
 * 사용자가 거치는 화면이 provider 동의 하나뿐이라 이 화면은 mlog가 자기를 소개할 유일한
 * 자리다. 기능을 늘어놓지 않고 소개와 안심 문장 두 줄로 끝낸다. 유일한 액션인 provider
 * 버튼은 엄지가 닿는 아래쪽에 둔다.
 *
 * 데이터를 부르지 않고 props로만 그린다. 주소를 읽고 왕복을 시작하는 일은 화면을 감싸는
 * 페이지가 맡는다.
 */
export function LoginScreen({
  onStart,
  status = 'idle',
  leavingProvider,
  reason,
  sessionExpired = false,
  failedProvider,
  termsHref = '/terms',
  privacyHref = '/privacy',
}: LoginScreenProps) {
  const m = useMessages()
  const establishing = status === 'establishing'
  const leaving = status === 'leaving'

  /*
   * 이름을 끼우는 문구인데 어느 쪽에서 났는지 모르면 원인을 가리지 못한 실패로 내린다.
   * 한쪽 이름을 기본값으로 두면 네이버에서 막힌 사람에게 구글을 다녀오라고 말하게 된다.
   */
  const effective =
    reason && (failedProvider || !FAILURE_NAMES_PROVIDER[reason]) ? reason : reason && 'unavailable'

  /*
   * 안내는 한 자리를 나눠 쓴다. provider에서 막힌 것과 시간이 지나 끊긴 것이 같은 순간에
   * 겹치지 않으므로 둘을 한 자리에 세운다.
   */
  const notice = effective
    ? failureMessage(m, effective, failedProvider ? m.auth.provider[failedProvider] : '')
    : sessionExpired
      ? m.auth.login.sessionExpired
      : null

  return (
    <main className="bg-canvas text-ink mx-auto flex h-full w-full max-w-[390px] flex-col px-6 pt-8 pb-7">
      <div className="min-h-18 flex-1" />

      <div className="flex flex-col items-center gap-2.5 text-center">
        <h1 className="text-3xl leading-[1.25] font-semibold tracking-[-0.02em]">mlog</h1>
        <p className="text-lg leading-[1.55]">{m.auth.login.tagline}</p>
        <p className="text-ink-muted text-base leading-[1.65]">{m.auth.login.assurance}</p>
      </div>

      <div className="min-h-22 flex-[1.6]" />

      {/*
       * 진행 문구와 안내 문구가 같은 알림 영역 하나를 쓴다. 끼어드는 수준으로 읽지 않는다.
       * 사용자가 스스로 버튼을 눌러 이 변화를 기다리고 있다.
       * 안내 블록의 자리는 하나이고 provider 버튼 바로 위다. 두 안내가 함께 뜨지 않는다.
       */}
      <div aria-live="polite" className="empty:hidden">
        {establishing ? (
          <p className="text-center text-base leading-[1.65]">{m.auth.login.establishing}</p>
        ) : (
          notice && (
            <div className="mb-3">
              {/*
               * 블록 안에 다시 시도 버튼을 두지 않는다. 다시 갈 곳은 아래 두 버튼 가운데
               * 하나이고 안내가 그 버튼을 가리키면 된다. 블록에 버튼을 두면 어느 곳으로
               * 가는지 라벨로 밝혀야 하고 화면에 세 번째 액션이 생긴다.
               */}
              <Notice>{notice}</Notice>
            </div>
          )
        )}
      </div>

      {!establishing && (
        <>
          <div className="flex flex-col gap-3">
            {/*
             * 버튼 둘을 항목 둘인 목록으로 묶는다. 화면을 소리로 듣는 사람이 첫 버튼에
             * 닿기 전에 고를 것이 둘이라는 것을 안다. 사이는 12px이고 8pt 그리드의 절반
             * 단계다. 16px을 주면 한 묶음으로 읽히지 않고 8px은 두 타깃이 붙어 보인다.
             */}
            <ul className="flex list-none flex-col gap-3">
              {PROVIDER_IDS.map((provider) => (
                <li key={provider}>
                  <ProviderButton
                    provider={provider}
                    pending={leaving && leavingProvider === provider}
                    locked={leaving && leavingProvider !== provider}
                    onClick={() => onStart(provider)}
                  />
                </li>
              ))}
            </ul>
            <p className="text-ink-muted text-center text-sm leading-[1.6]">
              {m.auth.login.autoSignUp}
            </p>
          </div>

          {/*
           * 동의 체크박스를 두지 않는다. provider 인증과 계정 생성 사이에 확인 단계를 다시
           * 세우기 때문이다. 크기만 한 단계 낮추고 대비는 본문과 같은 기준을 지킨다.
           * 링크는 줄 간격과 세로 여백으로 44px 높이를 확보한다.
           */}
          <p className="text-ink-muted mt-6 text-center text-sm leading-[1.6]">
            {m.auth.login.consent(
              <ConsentLink href={termsHref}>{m.auth.login.termsLink}</ConsentLink>,
              <ConsentLink href={privacyHref}>{m.auth.login.privacyLink}</ConsentLink>,
            )}
          </p>
        </>
      )}
    </main>
  )
}

/**
 * 막힌 일을 사용자 탓으로 돌리지 않고 다음에 할 일을 하나만 준다.
 *
 * 한쪽이 막혔을 때 다른 쪽을 권하지 않는다. 이 화면은 지금 누른 사람이 처음 온 사람인지
 * 돌아온 사람인지 알지 못한다. 돌아온 사람에게 다른 버튼은 로그인이 아니라 빈 공간이 하나
 * 더 서는 길이다. 두 버튼이 그대로 놓여 있어 스스로 고를 수 있으므로 안내는 밀지 않는다.
 *
 * 문구를 provider마다 따로 쓰지 않고 한 벌만 둔다. 이름이 들어가는 자리를 문구마다 하나로
 * 정하고 그 자리만 바꿔 끼운다. 이름을 넣는 자리는 사용자가 그쪽으로 가서 마저 할 일이 남은
 * 곳뿐이다. 어느 쪽 일인지 우리가 가리지 못했거나 사용자가 방금 다녀와 이미 아는 자리에는
 * 넣지 않는다.
 */
function failureMessage(m: Messages, reason: LoginFailureReason, provider: string): string {
  switch (reason) {
    case 'declined':
      return m.auth.failure.declined
    case 'email_already_registered':
      return m.auth.failure.emailAlreadyRegistered
    case 'email_unavailable':
      return m.auth.failure.emailUnavailable(provider)
    case 'unavailable':
      return m.auth.failure.unavailable
  }
}

/** 이름을 끼우는 문구가 어느 것인지. 어느 쪽에서 났는지 모르면 원인을 가리지 못한 실패로 내린다. */
const FAILURE_NAMES_PROVIDER: Record<LoginFailureReason, boolean> = {
  declined: false,
  email_already_registered: false,
  email_unavailable: true,
  unavailable: false,
}

/** 문장 안에 서는 자리라 세로 여백으로 44px 높이를 확보한다. */
function ConsentLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      className="focus-visible:ring-ink inline-block py-2.5 underline underline-offset-[3px] focus-visible:rounded-sm focus-visible:ring-2 focus-visible:outline-none"
    >
      {children}
    </a>
  )
}
